import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Check, Clock, Map, Bell, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const WA_BOOK = "https://wa.me/919819933676?text=Namaste!%20Mujhe%20Divya%20Dhara%20ka%201%20din%20ka%20%E2%82%B984%20trial%20book%20karna%20hai.%20Please%20contact%20karein.%20%F0%9F%A5%9B";
const WA_NOTIFY = (area) => `https://wa.me/919819933676?text=${encodeURIComponent(`Namaste! Mujhe Divya Dhara ki delivery ${area || "mere area"} me chahiye. Please notify karein jab available ho. 🙏`)}`;

const ZONE_STYLES = {
  active:  { ring: "border-green-500 bg-green-50",  chip: "bg-green-600",  icon: Check, title: "We deliver here!" },
  soon:    { ring: "border-amber-400 bg-amber-50",  chip: "bg-amber-500",  icon: Clock, title: "Coming Soon!" },
  future:  { ring: "border-sky-400 bg-sky-50",      chip: "bg-sky-600",    icon: Map,   title: "On our expansion list" },
  unknown: { ring: "border-gray-300 bg-gray-50",    chip: "bg-gray-500",   icon: Bell,  title: "Not yet in coverage" },
};

export default function PincodeChecker() {
  const { t } = useI18n();
  const [pincode, setPincode] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(pincode)) {
      setResult({ zone: "unknown", message: "Please enter a valid 6-digit pincode.", pincode });
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/pincode/check", { pincode });
      setResult(data);
    } catch {
      setResult({ zone: "unknown", message: "Could not reach the server. Please WhatsApp us.", pincode });
    } finally {
      setLoading(false);
    }
  };

  const renderCta = (zone, area) => {
    if (zone === "active") return (
      <a href={WA_BOOK} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-full text-sm font-bold" data-testid="pincode-book-btn">
        <MessageCircle className="w-4 h-4" /> Book on WhatsApp
      </a>
    );
    if (zone === "soon") return (
      <a href={WA_NOTIFY(area)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-5 py-2.5 rounded-full text-sm font-bold" data-testid="pincode-notify-btn">
        <Bell className="w-4 h-4" /> Notify Me When Available
      </a>
    );
    if (zone === "future") return (
      <a href={WA_NOTIFY(area)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white px-5 py-2.5 rounded-full text-sm font-bold" data-testid="pincode-register-btn">
        <Bell className="w-4 h-4" /> Register Interest
      </a>
    );
    return (
      <a href={WA_NOTIFY(area)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-5 py-2.5 rounded-full text-sm font-bold" data-testid="pincode-whatsapp-btn">
        <Bell className="w-4 h-4" /> Notify Me
      </a>
    );
  };

  return (
    <section className="dd-section bg-dd-ivory" id="delivery" data-testid="pincode-section">
      <div className="dd-container max-w-3xl">
        <div className="text-center mb-10">
          <div className="dd-label text-dd-gold">{t.delivery.eyebrow}</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-dd-cream mt-3">{t.delivery.title}</h2>
          <div className="dd-ornament my-5"><span className="text-lg">✦</span></div>
          <p className="text-dd-cream-muted">{t.delivery.subtitle}</p>
        </div>

        <form onSubmit={check} className="flex flex-col sm:flex-row gap-3" data-testid="pincode-form">
          <div className="flex-1 flex items-center gap-3 bg-white border border-dd-line rounded-full px-5 focus-within:border-dd-gold focus-within:ring-1 focus-within:ring-dd-gold">
            <MapPin className="w-5 h-5 text-dd-gold" strokeWidth={1.75} />
            <input
              inputMode="numeric"
              maxLength={6}
              placeholder="e.g. 400706"
              value={pincode}
              onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}
              className="w-full py-3.5 bg-transparent outline-none text-dd-ink placeholder:text-dd-ink-muted/60"
              data-testid="pincode-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="bg-gradient-to-b from-dd-gold to-[#8F6E28] hover:from-dd-gold-light hover:to-dd-gold text-dd-ink-dark px-8 py-3.5 rounded-full font-bold transition-all disabled:opacity-60 tracking-wide"
            data-testid="pincode-check-btn"
          >
            {loading ? "Checking…" : t.delivery.check}
          </button>
        </form>

        {result && (() => {
          const zone = result.zone || (result.serviceable ? "active" : "unknown");
          const s = ZONE_STYLES[zone] || ZONE_STYLES.unknown;
          const Icon = s.icon;
          return (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-6 rounded-xl border-2 p-5 flex items-start gap-4 ${s.ring}`}
              data-testid={`pincode-result-${zone}`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-white ${s.chip}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="font-serif text-xl text-dd-ink">{s.title}</div>
                <div className="text-sm text-dd-ink-muted mt-1">{result.message}</div>
                {result.area && <div className="text-xs text-dd-ink-muted mt-1">📍 {result.area} · Pincode {result.pincode}</div>}
                <div className="mt-4">{renderCta(zone, result.area)}</div>
              </div>
            </motion.div>
          );
        })()}

        <div className="text-center text-xs text-dd-cream-muted mt-6">{t.delivery.areas}</div>
      </div>
    </section>
  );
}
